import React, { useMemo } from "react";
import { Container, Row, Col } from "reactstrap";
import {
    DashboardPageHeader,
    CompactStatsRow,
    EmptyState,
} from "../../Components/Common/DashboardKit";
import { useCrSummary } from "./Form/useContractReviewApi";
import ContractReviewTabs from "./ContractReviewTabs";

/**
 * Contract Review landing page — widget row + tab strip.
 *
 * The legacy global "Search Contract Reviews" panel has been removed; each
 * tab now has its own row-level filter built into the table header, so users
 * can narrow down a specific tab without leaving the tab they're on.
 *
 * Pass 2 UI update (2026):
 *  - Replaced the inline CountUp+avatar-title stat cards with the shared
 *    DashboardKit StatTile via CompactStatsRow. The same four metrics are
 *    rendered with the same labels, counts, and hints — pure visual change.
 *  - Loading and error states now use EmptyState for consistency with the
 *    rest of the dashboard. Same trigger conditions, same fallback hierarchy.
 *  - useCrSummary, the data fetch hook, was NOT touched.
 */
const ContractReview = () => {
    document.title = "Contract Review | SS White Technologies Inc";

    const { data: summary, isLoading, error } = useCrSummary();
    const widgets = useMemo(() => buildWidgets(summary), [summary]);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-file-text-line"
                    eyebrow="Quality / Sales"
                    title="Contract Review"
                    subtitle="Author, route, and archive contract review records across the workflow."
                    tone="teal"
                    breadcrumb={[
                        { label: "Dashboard", to: "/" },
                        { label: "Contract Review" },
                    ]}
                />

                {/* Stat tiles — same four metrics as before, now via shared kit */}
                {error && !isLoading ? (
                    <Row className="mb-3">
                        <Col xs={12}>
                            <EmptyState
                                icon="ri-error-warning-line"
                                tone="danger"
                                title="Could not load summary"
                                message="The contract-review summary could not be fetched. The table below still works — try refreshing the page to retry."
                                compact
                            />
                        </Col>
                    </Row>
                ) : (
                    <CompactStatsRow
                        stats={widgets}
                        loading={isLoading}
                        columns={{ xxl: 3, sm: 6 }}
                        className="mb-3"
                    />
                )}

                <ContractReviewTabs />
            </Container>
        </div>
    );
};

/**
 * Build the StatTile-shaped stats array from the summary payload.
 *
 * NOTE: the field names on `s` (awaitingMe, readyForMe, inProgress, saved)
 * are the EXACT shape returned by useCrSummary and must NOT be renamed —
 * they correspond to backend property names on the CrSummary DTO.
 */
function buildWidgets(s) {
    if (!s) {
        // While loading, render four skeleton tiles with placeholder labels
        // so the page doesn't shift when data arrives.
        return [
            { key: "awaitingMe", label: "Awaiting My Response", value: 0, icon: "ri-inbox-unarchive-line", tone: "warning", loading: true },
            { key: "readyForMe", label: "Ready For My Review", value: 0, icon: "ri-checkbox-circle-line", tone: "success", loading: true },
            { key: "inProgress", label: "In Progress", value: 0, icon: "ri-time-line", tone: "info", loading: true },
            { key: "saved", label: "Saved (Pending Submit)", value: 0, icon: "ri-draft-line", tone: "secondary", loading: true },
        ];
    }
    return [
        {
            key: "awaitingMe",
            label: "Awaiting My Response",
            value: s.awaitingMe || 0,
            icon: "ri-inbox-unarchive-line",
            tone: "warning",
        },
        {
            key: "readyForMe",
            label: "Ready For My Review",
            value: s.readyForMe || 0,
            icon: "ri-checkbox-circle-line",
            tone: "success",
        },
        {
            key: "inProgress",
            label: "In Progress",
            value: s.inProgress || 0,
            icon: "ri-time-line",
            tone: "info",
        },
        {
            key: "saved",
            label: "Saved (Pending Submit)",
            value: s.saved || 0,
            icon: "ri-draft-line",
            tone: "secondary",
        },
    ];
}

export default ContractReview;
